/***********************/
/* Angular + external */
import { Injectable, ViewContainerRef } from "@angular/core";
import { Observable, Subscription } from "rxjs";
/***********************/
/* BFX */
// models
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxBlotterActionEvent } from "../../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { BfxDialogData } from "../models/bfx-dialog-data.model";
// services
import { BfxConfigService } from "../../../shared/auth-services/system-config/bfx-config.service";
import { TranslateService } from "@ngx-translate/core";
import { BfxRBACService } from "../../bfx-common/services/bfx-rbac.service";
import {
  TraderGroupPrimaryLookupService,
  SystemConfigPromiseService,
  CcyPromiseLookupService,
  BranchPromiseLookupService,
  OrderDataActionPromiseService,
  ClientLookupService,
  ClientAccountVisibilityType,
  IOrderNotifData,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
import { isOrderNotifData } from "../../../shared/cubiq-services/model/core/notifications/IOrderNotifData";
const logger = LoggerService.for(__dirname);

type STPCheckboxState = {
  show: boolean;
  state: boolean;
  disabled: boolean;
};

@Injectable()
export class BfxOrderDialogsProcessorService {
  /***************************************/
  /* DATA */
  public config: any = {
    debug: false,
  };

  private configurationSubscription: Subscription;

  private hasQuickFillOverride: boolean = false;
  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(
    private _configService: BfxConfigService,
    private _translationService: TranslateService,
    private _rbacService: BfxRBACService,
    private _traderGroupLookupService: TraderGroupPrimaryLookupService,
    private _ccyLookupService: CcyPromiseLookupService,
    private _clientLookupService: ClientLookupService,
    private _branchLookupService: BranchPromiseLookupService,
    private _orderDataActionService: OrderDataActionPromiseService,
    private _systemConfigService: SystemConfigPromiseService,
  ) {
    this.subscribeToConfiguration();
  }

  /**********************************************************************/
  /* INITIALIZATION */
  private subscribeToConfiguration(): void {
    this.configurationSubscription = this._configService.uiConfig$.subscribe((config) => {
      this.config = config.general;
    });
  }
  /**********************************************************************/
  /* QUICK FILL CONFIRM */
  public executeQuickFill(selectedIds: Array<string>, checkboxState: boolean): Promise<any> {
    return new Promise((resolve, reject) => {
      if (event != null && selectedIds != null) {
        let orderIds = selectedIds;
        let stpFg = null;

        if (selectedIds.length > 1 || !this.hasQuickFillOverride) {
          stpFg = CubiqTS.FillStpRequirement.STP_AS_NEEDED;
        } else {
          if (checkboxState) {
            stpFg = CubiqTS.FillStpRequirement.STP_ALL;
          } else {
            stpFg = CubiqTS.FillStpRequirement.STP_NONE;
          }
        }

        this._orderDataActionService.quickFillOrders(orderIds, stpFg).then((response) => {
          this.config.debug
            ? logger.out.info(
                "ORDER-ACTION: Quick Fill Orders:  orderIDs= ",
                orderIds,
                "response=",
                response,
              )
            : "";
          if (response.results != null && response.results.length > 0) {
            for (let result of response.results) {
              if (
                result.errors != null &&
                result.errors.errorMessages != null &&
                result.errors.errorMessages.length > 0
              ) {
                // let errors: CubiqTS.ErrorsImpl = result.errors as CubiqTS.ErrorsImpl;
                try {
                  let msg: string = this.handleErrors(result.errors.errorMessages); // errors.errorMessages[0].toString();
                  logger.out.error("Quick Fill FAILED", "ERROR: " + msg);
                } catch (ex) {
                  logger.out.error(
                    "Quick Fill FAILED",
                    "ERROR: " + result.errors.errorMessages[0].toString(),
                  );
                }
              }
            }
          }
          resolve(response);
        });
      } else {
        logger.out.error(
          "ERROR: BfxOrderDialogsProcessorService.executeQuickFill(): event or event.selectedIds is NULL!",
        );
        reject("Error!");
      }
    });
  }

  private handleErrors(errors: any[]): string {
    let message = "";
    errors
      .map((e) => JSON.parse(e))
      .forEach((e) => (message += this._translationService.instant(e["id"], e["args"]) + "\n"));
    return message;
  }

  /**
   * function: evaluateSTPCheckboxState()
   * @param event - event with
   *
   * Is checkbox visible? if multiple orders "No", if single order "Yes"
   * Is checkbox editable? if user has ROLE_FILL_STP_OVERRIDE permission "Yes", if no permissions "No"
   * Is checkbox checked?
   * 	- Client
   *  - Client Branch
   * 	- Trader Group Branch
   *  - Ccy (When filling a pair, both Ccy's must be STP enabled.)
   */
  public async evaluateSTPCheckboxState(event: BfxBlotterActionEvent): Promise<STPCheckboxState> {
    let selectedItems: Array<CubiqTS.OrderDataImpl | IOrderNotifData> = event?.selectedItems ?? [];

    if (Array.isArray(selectedItems) && selectedItems.length !== 1) {
      return {
        show: false,
        state: false,
        disabled: true,
      };
    }

    try {
      let order: CubiqTS.OrderDataImpl | null = isOrderNotifData(selectedItems[0])
        ? selectedItems[0].fullOrderDataObj
        : event.selectedItems[0];

      if (order == null || order.requestedOrderType === CubiqTS.RequestOrderType.CALL) {
        // STP should not be available for call
        return { show: false, state: false, disabled: true };
      }

      let role = await this._rbacService.hasRole("ROLE_ORDER_FILL_STP_OVERRIDE");
      let tradingGroup = await this._traderGroupLookupService.getItem(order.deskId?.toString());
      let baseCcy = await this._ccyLookupService.getCcy(order.baseCode);
      let nonBaseCcy = await this._ccyLookupService.getCcy(order.nonBaseCode);
      let client: CubiqTS.ClientImpl = this._clientLookupService.getClient(
        order.clientCode,
        ClientAccountVisibilityType.Full,
      );
      let clientBranch: CubiqTS.BranchImpl = await this._branchLookupService.getBranch(
        order.clientBranchCode,
      );
      let tradingBranch: CubiqTS.BranchImpl = await this._branchLookupService.getBranch(
        tradingGroup.branchCode,
      );

      const stp = {
        client: client?.straightThroughProcessingFg ?? true,
        clientBranch: clientBranch?.straightThroughProcessingFg ?? false,
        tradingBranch: tradingBranch?.straightThroughProcessingFg ?? false,
        baseCcy: baseCcy?.straightThroughProcessingFg ?? false,
        nonBaseCcy: nonBaseCcy?.straightThroughProcessingFg ?? false,
        partialBranches: await this._systemConfigService.getSystemConfigBoolean(
          "system.features.on_partial_branches_enabled",
        ),

        get enabled(): boolean {
          return (
            (this.client &&
              this.baseCcy &&
              this.nonBaseCcy &&
              this.partialBranches &&
              (this.clientBranch || this.tradingBranch)) ||
            (this.client &&
              this.baseCcy &&
              this.nonBaseCcy &&
              this.clientBranch &&
              this.tradingBranch)
          );
        },
      };

      this.hasQuickFillOverride = role;

      return {
        show: true,
        state: stp.enabled,
        disabled: !stp.enabled || !role,
      };
    } catch (ex) {
      logger.out.error(
        "ERROR: BfxOrderDialogsComponent.evaluateSTPCheckboxState(): exception= ",
        ex,
      );
      return { show: false, state: false, disabled: true };
    }
  }

  /**********************************************************************/
  /* QUICK FILL WARNING */
  public prepareQuickFillWarningMessageText(
    _messageType: string,
    _dialogData: BfxDialogData,
    localData: any,
  ): Promise<string | null> {
    return new Promise<string>((resolve) => {
      try {
        let warningData: any = _dialogData.payload;
        let message_text: string = "";

        if (_messageType === "external") {
          // EXTERNAL ORDER WARNING
          if (!warningData.external.display) {
            resolve(null);
          } else {
            if (warningData.external.ids.length === 1) {
              // single order
              let order: any = _dialogData.event.selectedItems.filter((item: any) => {
                return item.orderId.toString() === warningData.expired.ids[0];
              })[0];
              message_text = localData.message_text_template.external_single;
              message_text = message_text.replace("=Id=", warningData.external.orderRefs[0]);
            } else if (warningData.external.ids.length > 1) {
              // multiple orders
              message_text = localData.message_text_template.external_multi;
              message_text = message_text.replace("=Num=", warningData.external.ids.length);
            } else {
              // error
              logger.out.error(
                "ERROR: OrderDialogComponent.prepareQuickFillWarningMessageText(): unknown orders in external!",
              );
              message_text = "Error!";
            }
            resolve(message_text);
          }
        } else if (_messageType === "expired") {
          // EXPIRED ORDER WARNING
          if (!warningData.expired.display) {
            resolve(null);
          } else {
            if (warningData.expired.ids.length === 1) {
              // single
              let order: any = _dialogData.event.selectedItems.filter((item: any) => {
                return item.orderId.toString() === warningData.expired.ids[0];
              })[0];
              message_text = localData.message_text_template.expired_single;
              message_text = message_text.replace("=Id=", warningData.expired.orderRefs[0]);
              message_text = message_text.replace("=Time=", order.expiryDts);
            } else if (warningData.expired.ids.length > 1) {
              // multi
              message_text = localData.message_text_template.expired_multi;
              message_text = message_text.replace("=Num=", warningData.expired.ids.length);
            } else {
              // error
              logger.out.error(
                "ERROR: OrderDialogComponent.prepareQuickFillWarningMessageText(): unknown orders in expired!",
              );
              message_text = "Error!";
            }
            resolve(message_text);
          }
        } else if (_messageType === "transfer") {
          // TRANSFER ORDER WARNING
          if (!warningData.transfer.display) {
            resolve(null);
          } else {
            if (warningData.transfer.ids.length === 1) {
              // single
              let order: any = _dialogData.event.selectedItems.filter((item: any) => {
                return item.orderId.toString() === warningData.transfer.ids[0];
              })[0];
              this._traderGroupLookupService
                .getTraderGroupForSid(order.targetDeskId.toString())
                .then((tradingGroup: CubiqTS.TradingGroupImpl) => {
                  message_text = localData.message_text_template.transfer_single;
                  message_text = message_text.replace("=Id=", warningData.transfer.orderRefs[0]);
                  message_text = message_text.replace("=Loc=", tradingGroup.groupCode);
                  resolve(message_text);
                });
            } else if (warningData.transfer.ids.length > 1) {
              // multi
              message_text = localData.message_text_template.transfer_multi;
              message_text = message_text.replace("=Num=", warningData.transfer.ids.length);
              resolve(message_text);
            } else {
              // error
              logger.out.error(
                "ERROR: OrderDialogComponent.prepareQuickFillWarningMessageText(): unknown orders in transfer!",
              );
              message_text = "Error!";
              resolve(message_text);
            }
          }
        } else if (_messageType === "credit_check") {
          if (!warningData.credit_check.display) {
            resolve(null);
          } else {
            if (warningData.credit_check.ids.length === 1) {
              // single
              message_text = localData.message_text_template.credit_check_single;
              message_text = message_text.replace("=Id=", warningData.credit_check.orderRefs[0]);
            } else if (warningData.credit_check.ids.length > 1) {
              // multi
              message_text = localData.message_text_template.credit_check_multi;
              message_text = message_text.replace("=Num=", warningData.credit_check.ids.length);
            } else {
              // error
              logger.out.error(
                "ERROR: OrderDialogComponent.prepareQuickFillWarningMessageText(): unknown orders in credit_check!",
              );
              message_text = "Error!";
            }
            resolve(message_text);
          }
        } else if (_messageType === "fill") {
          if (!warningData.fill.display) {
            resolve(null);
          } else {
            message_text = localData.message_text_template.fill;
            message_text = message_text.replace("=Num=", warningData.fill.ids.length.toString());
            resolve(message_text);
          }
        } else {
          // unknown messageType
          resolve("Error!");
        }
      } catch (ex) {
        logger.out.error(
          "ERROR: OrderDialogComponent.openQuickFillWarningDialog(): Exception! msg:",
          ex,
        );
        resolve("Error!");
      }
    });
  }
}
